import * as React from 'react';

/**
 * This hook allows components to use the reducer pattern to manage state that they do not explicitly own
 * (i.e. where they did not have the opportunity to initialize the state using `useReducer`).
 *
 * By passing in a `setState` updater function (which likely came from a custom hook such as `useInlineEditor`)
 * and a `reducer` function, this hook returns a `dispatch` function, like the one returned by `React.useReducer`,
 * which accepts actions and makes the appropriate underlying `setState` calls according to the reducer.
 *
 * For a usage example, see the accompanying unit tests.
 *
 * **Note: Unlike the conventional `useReducer`, the returned `dispatch` instance will change if a
 * new reducer is passed in, hence it must be specified in subsequent hooks' dependency arrays.
 * This lack of memoization should be fine for most use cases, but if you need to memoize the dispatch
 * function, you must do it yourself using `React.useMemo`.**
 *
 * For a usage example, see the accompanying unit tests.
 *
 * @param setState The setState handler you have access to
 * @param reducer The reducer representing the state update logic
 */
export const useSetStateAsReducer = convertStateUpdaterToDispatch;

/**
 * Builds a `dispatch` function that can be used to update state (via the `setState` function)
 * according to the given `reducer`. This allows us to use reducers with APIs that expose only a
 * state updater.
 *
 * If using this within a React component, use `useSetStateAsReducer`, which is the same thing
 * but exposed as a React hook.
 *
 * @param setState The setState handler you have access to
 * @param reducer The reducer representing the state update logic
 */
export function convertStateUpdaterToDispatch<S, A>(
  setState: React.Dispatch<React.SetStateAction<S>>,
  reducer: React.Reducer<S, A>
): React.Dispatch<A> {

  return function dispatch(action: A) {
    setState(prevState => reducer(prevState, action))
  };
}
